import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Inbox, ExternalLink, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useContent } from "@/contexts/ContentContext";

const SECTION_LINKS = [
  { key: "site", label: "Site / Brand" },
  { key: "hero", label: "Hero" },
  { key: "services", label: "Services" },
  { key: "about", label: "About" },
  { key: "portfolio", label: "Portfolio" },
  { key: "process", label: "How It Works" },
  { key: "testimonials", label: "Testimonials" },
  { key: "areas", label: "Service Areas" },
  { key: "cta", label: "CTA Banner" },
  { key: "contact", label: "Contact" },
  { key: "footer", label: "Footer" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { reset, reload } = useContent();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/admin/login");
  };

  const onReset = async () => {
    if (!window.confirm("Reset ALL content to defaults? This cannot be undone.")) return;
    try {
      await reset();
      toast.success("Content reset to defaults");
    } catch (e) {
      toast.error("Failed to reset content");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-[#0E0E0E] border-r border-white/5 sticky top-0 h-screen">
        <div className="px-6 py-7 border-b border-white/5 flex items-center gap-3">
          <Logo size={40} variant="mark" />
          <div className="leading-tight">
            <div className="logo-mark text-white text-base">M&amp;C</div>
            <div className="logo-mark text-[10px] tracking-[0.3em] text-[#D4AF37]">CLEAN FRESH</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          <NavLink
            to="/admin"
            end
            data-testid="back-to-hub"
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all text-white/55 hover:bg-white/[0.04] hover:text-[#D4AF37] mb-2"
          >
            <span aria-hidden="true">←</span> Back to hub
          </NavLink>
          <NavLink
            to="/admin/cms"
            end
            data-testid="nav-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                isActive ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/65 hover:bg-white/[0.04] hover:text-white"
              }`
            }
          >
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink
            to="/admin/cms/quotes"
            data-testid="nav-quotes"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
                isActive ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/65 hover:bg-white/[0.04] hover:text-white"
              }`
            }
          >
            <Inbox className="w-4 h-4" /> Quote Requests
          </NavLink>

          <div className="pt-5 pb-2 px-4 text-[10px] uppercase tracking-[0.3em] text-white/35 font-bold">
            Page Sections
          </div>

          {SECTION_LINKS.map((s) => (
            <NavLink
              key={s.key}
              to={`/admin/cms/section/${s.key}`}
              data-testid={`nav-section-${s.key}`}
              className={({ isActive }) =>
                `block px-4 py-2 rounded-xl text-sm transition-all ${
                  isActive ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30" : "text-white/65 hover:bg-white/[0.04] hover:text-white"
                }`
              }
            >
              {s.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 px-4 py-4 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-site"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-white/65 hover:text-[#D4AF37] hover:bg-white/[0.04] transition-all"
          >
            <ExternalLink className="w-4 h-4" /> View live site
          </a>
          <button
            onClick={onReset}
            data-testid="reset-content"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-white/65 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <RotateCcw className="w-4 h-4" /> Reset to defaults
          </button>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs text-white/65 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <div className="pt-2 text-[10px] text-white/30 truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 px-4 flex items-center justify-between bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-2">
          <Logo size={32} variant="mark" />
          <span className="logo-mark text-white text-sm">M&amp;C <span className="text-[#D4AF37]">Clean Fresh</span></span>
        </div>
        <button onClick={onLogout} className="text-xs text-white/70 hover:text-red-400 flex items-center gap-1.5">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        {/* Mobile section nav */}
        <div className="lg:hidden border-b border-white/5 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-4 py-3">
            <NavLink end to="/admin/cms" className={({ isActive }) => `shrink-0 px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-white/5 text-white/70"}`}>Dashboard</NavLink>
            <NavLink to="/admin/cms/quotes" className={({ isActive }) => `shrink-0 px-3 py-1.5 rounded-full text-xs ${isActive ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-white/5 text-white/70"}`}>Quotes</NavLink>
            {SECTION_LINKS.map((s) => (
              <NavLink key={s.key} to={`/admin/cms/section/${s.key}`} className={({ isActive }) => `shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-white/5 text-white/70"}`}>{s.label}</NavLink>
            ))}
          </div>
        </div>

        <Outlet />
      </main>
    </div>
  );
}
